import React from 'react'
import { useTranslation } from 'react-i18next'
import { KibCheckbox } from '@chewy/kib-choices-react'
import CloseIcon from '@mui/icons-material/Close'
import { Grid, IconButton, Stack } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { ButtonWithLoader, PuiTextField, Text } from '@pbt/pbt-ui-components'
import { WarningRound as WarningIcon } from '@pbt/pbt-ui-components/src/icons'

import { ConversationTransport } from '~/api/graphql/generated/types'
import SystemMessageAlert from '~/components/dashboard/communications/conversations-chat/SystemMessageAlert'
import { messageFormattingAreaFocused } from '~/store/actions/conversationMessages'
import { useMessageFormatting } from '~/store/hooks/conversations'

import ConversationMessageFormattingArea from '../ConversationMessageFormattingArea'

import '@chewy/kib-choices-styles/dist/styles/kib-checkbox.css'
import styles from './MessageFormattingArea.module.scss'

const useStyles = makeStyles(
  (theme) => ({
    root: {
      position: 'relative',
      borderRadius: 2,
      borderTop: theme.constants.tableBorder,
      backgroundColor: theme.colors.grayWhite,
    },
    button: {
      height: 40,
    },
    warningIcon: {
      color: theme.colors.uiNeedsAttention,
      width: 24,
      height: 24,
    },
    secondaryButton: {
      color: theme.colors.brandPrimary,
    },
    closeIcon: {
      color: theme.colors.grayGray1,
      width: 20,
      height: 20,
    },
    closeButton: {
      position: 'absolute',
      top: theme.spacing(0.5),
      right: theme.spacing(0.5),
    },
  }),
  { name: 'MessageFormattingArea' },
)

export interface MessageFormattingAreaProps {
  conversationId: string
  disabled?: boolean
  hideStatusUpdateButtons?: boolean
}

const MessageFormattingArea = ({
  conversationId,
  disabled = false,
  hideStatusUpdateButtons = false,
}: MessageFormattingAreaProps) => {
  const classes = useStyles()
  const { t } = useTranslation(['Common', 'Communications', 'Clients'])
  const {
    messageFormattingAreaExpanded,
    handleCloseMessageFormattingArea,
    canSendMessage,
    clientId,
    conversation,
    isCommunicationRightRailEnabled,
    messageField,
    patientId,
    appointment,
    conversationFormattingAreaRef,
    transport,
    isMarkMessagesAsMedicalEnabled,
    dispatch,
    internalNoteOnly,
    showSendButton,
    isLoading,
    handleMessageSend,
    showReplyButton,
    handleReplyClick,
    handleMessageDraftSend,
    shouldShowCheckbox,
    isMedical,
    onInternalNoteAdd,
    isDeliveryFailed,
    onMarkUnread,
    onArchiveAction,
    archiveActionTitle,
    isSystemMessage,
  } = useMessageFormatting({ conversationId })

  if (isSystemMessage && transport === ConversationTransport.Email) {
    return (
      <Grid container className={classes.root}>
        <SystemMessageAlert {...{ clientId, patientId }} />
      </Grid>
    )
  }

  return (
    <Grid
      container
      className={classes.root}
      pb={2}
      pt={messageFormattingAreaExpanded ? 5 : 2}
      px={3}
    >
      {messageFormattingAreaExpanded && (
        <IconButton
          disableRipple
          className={classes.closeButton}
          size="large"
          onClick={handleCloseMessageFormattingArea}
        >
          <CloseIcon className={classes.closeIcon} />
        </IconButton>
      )}
      <Grid container gap={2}>
        {canSendMessage && messageFormattingAreaExpanded && (
          <Grid container>
            <ConversationMessageFormattingArea
              hideEmail
              hideTo
              showAttachment
              clientId={clientId}
              eventId={conversation?.eventId}
              isChirp={isCommunicationRightRailEnabled}
              isShowSubject={false}
              maxEditorHeight={200}
              messageField={messageField}
              minEditorHeight={150}
              patientId={patientId || appointment?.patient}
              ref={conversationFormattingAreaRef}
              transport={transport}
            />
          </Grid>
        )}
        {!isMarkMessagesAsMedicalEnabled &&
          canSendMessage &&
          !messageFormattingAreaExpanded && (
            <Grid container>
              <PuiTextField
                margin="none"
                minRows={1}
                placeholder={t(
                  'Communications:MESSAGE_FORMATTING_AREA.TYPE_REPLY',
                )}
                variant="outlined"
                onFocus={() => {
                  if (!disabled) {
                    dispatch(messageFormattingAreaFocused())
                  }
                }}
              />
            </Grid>
          )}
        <Grid container>
          {!internalNoteOnly && (
            <Grid
              container
              item
              columnSpacing={1}
              mr={isMarkMessagesAsMedicalEnabled ? 1 : 0}
              xs={isMarkMessagesAsMedicalEnabled ? 'auto' : true}
            >
              <Grid
                container
                item
                xs
                alignItems="center"
                columnSpacing={1}
                wrap="nowrap"
              >
                {canSendMessage ? (
                  <>
                    {showSendButton && (
                      <Grid item>
                        <ButtonWithLoader
                          disabled={isLoading || !messageFormattingAreaExpanded}
                          onClick={handleMessageSend}
                        >
                          {t('Common:SEND_ACTION')}
                        </ButtonWithLoader>
                      </Grid>
                    )}
                    {showReplyButton && (
                      <Grid item>
                        <ButtonWithLoader
                          disabled={isLoading || disabled}
                          onClick={handleReplyClick}
                        >
                          {t('Common:REPLY')}
                        </ButtonWithLoader>
                      </Grid>
                    )}
                  </>
                ) : (
                  <Grid item>
                    <Stack alignItems="center" direction="row" spacing={1}>
                      <WarningIcon className={classes.warningIcon} />
                      <Text variant="body2">
                        {t('Clients:SMS_CONSENT_DISABLED')}
                      </Text>
                    </Stack>
                  </Grid>
                )}
                {messageFormattingAreaExpanded && (
                  <>
                    <Grid item>
                      <ButtonWithLoader
                        className={classes.secondaryButton}
                        color="secondary"
                        disabled={isLoading}
                        onClick={handleMessageDraftSend}
                      >
                        {t('Common:SAVE_AS_DRAFT')}
                      </ButtonWithLoader>
                    </Grid>

                    {shouldShowCheckbox && (
                      <Grid item className={styles.checkboxGrid}>
                        <KibCheckbox
                          className={styles.checkbox}
                          label={isMedical.label}
                          selectedValue={isMedical.value}
                          onChange={() => isMedical.setValue(!isMedical.value)}
                        />
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            </Grid>
          )}

          <Grid
            container
            item
            columnSpacing={1}
            flex={2}
            justifyContent={
              isMarkMessagesAsMedicalEnabled ? 'flex-start' : 'flex-end'
            }
            rowSpacing={1.5}
            sm={8}
          >
            {!messageFormattingAreaExpanded && (
              <>
                <Grid item>
                  <ButtonWithLoader
                    className={classes.secondaryButton}
                    color="secondary"
                    disabled={isLoading}
                    onClick={onInternalNoteAdd}
                  >
                    {t('Common:ADD_INTERNAL_NOTE')}
                  </ButtonWithLoader>
                </Grid>

                {!hideStatusUpdateButtons && !isDeliveryFailed && (
                  <>
                    <Grid item>
                      <ButtonWithLoader
                        className={classes.secondaryButton}
                        color="secondary"
                        disabled={isLoading}
                        onClick={onMarkUnread}
                      >
                        {t('Common:MARK_AS_UNREAD')}
                      </ButtonWithLoader>
                    </Grid>

                    <Grid item>
                      <ButtonWithLoader
                        className={classes.secondaryButton}
                        color="secondary"
                        disabled={isLoading}
                        onClick={onArchiveAction}
                      >
                        {archiveActionTitle}
                      </ButtonWithLoader>
                    </Grid>
                  </>
                )}
              </>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}

export default MessageFormattingArea
